import { getAudioFileDuration } from '@/features/upload-files'
import { type UploadFileWithId } from '@/features/upload-files'
import { type RcFile } from 'antd/es/upload'
import { createEffect, createEvent, createStore, sample } from 'effector'
import { createGate } from 'effector-react'

import {
  type TrackFileForMultipleUpload,
  type UploadTrackMultipleFilesToAlbumPayload,
  uploadTrackMultipleFilesFx,
} from './album-track-multiupload.model'

export function createAlbumTracksMultipleUploadModel() {
  const Gate = createGate<{ albumId: string }>()
  const albumIdInit = createEvent<string>()
  const filesInit = createEvent<UploadFileWithId[]>()
  const filesAdded = createEvent<RcFile[]>()
  const fileRemoved = createEvent<UploadFileWithId>()
  const allFilesRemoved = createEvent()
  const fileStartUpload = createEvent<Pick<UploadFileWithId, 'uid' | 'id'>>()
  const fileUploadSuccess = createEvent<Pick<UploadFileWithId, 'uid' | 'url'>>()
  const fileUploadError = createEvent<Pick<UploadFileWithId, 'uid' | 'error'>>()

  // Files Store
  const $files = createStore<UploadFileWithId[]>([])

  $files
    .on(filesInit, (_, files) => files)
    .on(filesAdded, (state, files) => [
      ...state,
      ...files.map(
        (file) =>
          ({
            uid: file.uid,
            name: file.name,
            size: file.size,
            type: file.type,
            originFileObj: file,
          }) as UploadFileWithId,
      ),
    ])
    .on(fileRemoved, (files, file) => {
      const index = files.indexOf(file)
      const newState = files.slice()
      newState.splice(index, 1)
      return newState
    })
    .on(allFilesRemoved, () => {
      return []
    })
    .on(fileStartUpload, (files, { uid, id }) => {
      return files.map((file) => {
        if (file.uid === uid) {
          file.status = 'uploading'
          file.id = id
        }
        return file
      })
    })

    .on(fileUploadSuccess, (files, payload) => {
      // remove file from store
      const index = files.findIndex((file) => file.uid === payload.uid)
      if (index === -1) return files
      const refreshedFiles = files.slice()
      refreshedFiles.splice(index, 1)
      return refreshedFiles
    })
    .on(fileUploadError, (files, { uid, error }) => {
      return files.map((file) => {
        if (file.uid === uid) {
          file.status = 'error'
          file.error = error
        }
        return file
      })
    })

  // Send files data to upload

  const buildUploadTrackMultipleFilesToAlbumPayloadFx = createEffect(
    async ({
      albumId,
      files,
    }: {
      albumId: string
      files: RcFile[]
    }): Promise<UploadTrackMultipleFilesToAlbumPayload> => {
      const tracks: TrackFileForMultipleUpload[] = await Promise.all(
        files.map(async (file) => {
          const duration = await getAudioFileDuration(file)
          return {
            uid: file.uid,
            file: file as File,
            params: {
              key: file.uid,
              fileName: file.name,
              duration: duration,
            },
          } as TrackFileForMultipleUpload
        }),
      )

      const payload: UploadTrackMultipleFilesToAlbumPayload = {
        albumId,
        tracks,
        fileStartUpload: fileStartUpload,
        fileUploadSuccess: fileUploadSuccess,
        fileUploadError: fileUploadError,
      }

      return payload
    },
  )

  sample({
    source: Gate.state,
    clock: filesAdded,
    fn: ({ albumId }, files) => {
      return { albumId, files }
    },
    target: buildUploadTrackMultipleFilesToAlbumPayloadFx,
  })

  sample({
    clock: buildUploadTrackMultipleFilesToAlbumPayloadFx.doneData,
    fn: (payload) => {
      return payload
    },
    target: uploadTrackMultipleFilesFx,
  })

  // Abort Controller

  const abortController = createStore<AbortController | null>(null)

  sample({
    clock: Gate.open,
    fn: () => new AbortController(),
    target: abortController,
  })

  sample({
    source: abortController,
    clock: filesInit,
    fn: (controller, files) => {
      if (!controller) throw new Error('AbortController is null')
      return { files, signal: controller.signal }
    },
  })

  sample({
    clock: Gate.close,
    source: abortController,
    filter: Boolean,
    fn: (controller) => {
      controller.abort()
    },
    target: allFilesRemoved,
  })

  return {
    Gate,
    $files,
    albumIdInit,
    filesInit,
    filesAdded,
    fileUploadSuccess,
    fileRemoved,
    allFilesRemoved,
  }
}
